// useExplorerData is the hook consumers use to fetch JSON for Explorer panels.
// The consumer passes a params object (typically QueryParams.parse(search))
// and a list of param field names whose changes should trigger a re-fetch.
//
// Re-fetching is keyed on JSON.stringify(pick(params, reloadOn)), so a
// parent re-render with the same content (different reference) is a no-op.
// The version-counter check inside the callback guards against a stale
// in-flight request resolving after the next effect has already started.

import { useEffect, useMemo, useRef, useState } from 'react';
import { pick } from 'lodash-es';

import * as QueryFetcher from 'components/Explorer/models/query_fetcher';

export const Loading = {
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  ERROR: 'error',
  EMPTY_RESPONSE: 'empty-response',
  SUCCESS: 'success'
};

const useExplorerData = (path, params, { reloadOn = [], enabled = true } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Loading.NOT_STARTED);
  const [status, setStatus] = useState(null);

  // Keep latest params accessible without baking them into the effect's
  // deps — we want re-fetches keyed on reloadOn, not on every render.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const versionRef = useRef(0);
  const reloadKey = reloadOn.join('|');
  const key = useMemo(() => JSON.stringify(pick(params, reloadOn)), [params, reloadKey]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(Loading.NOT_STARTED);
      setStatus(null);
      return undefined;
    }

    const version = ++versionRef.current;
    setLoading(Loading.IN_PROGRESS);

    const request = QueryFetcher.fetchJSON(path, paramsRef.current);
    request.then((err, response) => {
      if (version !== versionRef.current) return;
      if (err?.name === 'AbortError') return;

      setStatus(response?.status ?? null);

      if (err || typeof response?.body === 'undefined') {
        setData(null);
        setLoading(Loading.ERROR);
        return;
      }
      if (Array.isArray(response.body) && response.body.length === 0) {
        setData(response.body);
        setLoading(Loading.EMPTY_RESPONSE);
        return;
      }
      setData(response.body);
      setLoading(Loading.SUCCESS);
    });

    return () => request.abort();
  }, [path, key, enabled]);

  return { data, loading, status };
};

export default useExplorerData;
